import { DateTime } from 'luxon'
import _get from 'lodash/get'
import _isEmpty from 'lodash/isEmpty'
import API from './constants'

function _decorateTitle (title) {
  if (title) {
    return title.toString()
      .normalize('NFD')
      .replace(/[\u0300-\u036F]/g, '')
      .replace(/\s+/g, '-')
      .replace(/&/g, '-and-')
      .replace(/'/g, '-')
      .toLowerCase()
      .replace(/[^a-z0-9-]/g, '')
      .replace(/-+/g, '-')
      .replace(/^-*/, '')
      .replace(/-*$/, '')
  }
}


/**
 * Get parameters for the link based on the historyItem
 * @param item
 * @returns {{itemTitle: string, id: (*)}|{episodeTitle: string, id: (*)}}
 */
function _getFipRouteParams (item) {
  if (item.videoType === API.RESOURCE_TYPES.EPISODE) {
    return {
      seasonId: _get(item, 'seasonInfo.id'),
      seasonTitle: _decorateTitle(_get(item, 'seasonInfo.title')),
      id: item.type === API.RESOURCE_TYPES.SERIES ? item.browsingSeasonId : item.id,
      episodeTitle: _decorateTitle(item.title)
    }
  } else {
    return {
      id: item.type === API.RESOURCE_TYPES.SERIES ? item.browsingSeasonId : item.id,
      itemTitle: _decorateTitle(_get(item, 'seasons[0].title', item.title))
    }
  }
}

/**
 * Retrieve correct fip route name
 * @param type
 * @param videoType
 * @returns {string}
 */
function _getFipRouteName (type, videoType) {
  switch (type) {
    case API.RESOURCE_TYPES.CATEGORY:
    case API.RESOURCE_TYPES.DISCOUNT_CATEGORY:
      return API.FIP_ROUTE.CATEGORY

    case API.RESOURCE_TYPES.PACKAGE:
      return API.FIP_ROUTE.PACKAGE

    case API.RESOURCE_TYPES.SEASON:
    case API.RESOURCE_TYPES.SERIES:
      return API.FIP_ROUTE.SEASON

    case API.RESOURCE_TYPES.VIDEO:
      return videoType === API.RESOURCE_TYPES.EPISODE ? API.FIP_ROUTE.EPISODE : API.FIP_ROUTE.MOVIE

    default:
      return ''
  }
}

/**
 * Retrieve correct URL from cover/banner based on a specific format
 * @param item
 * @param format
 * @returns {null}
 */
function _getCoverUrl (item) {
  const images = _get(item, 'images', [])
  const covers = _get(item, 'covers', [])
  const image = [...images, ...covers].find(item => item.format === API.IMAGE_TYPE.COVER || item.format === API.IMAGE_TYPE.COVER_16_9)
  return image ? image.url : null
}

/**
 * Get Aggregate rating if available
 * @param item
 * @returns {{}}
 */
function _getAggregateRating (item) {
  const data = {}

  const ratingCount = _get(item, 'allocineRatings.user.count', null)
  const ratingValue = _get(item, 'allocineRatings.user.rating', null)

  if (ratingCount && ratingValue) {
    data.aggregateRating = {
      '@type': 'AggregateRating',
      ratingCount: ratingCount > 0 ? ratingCount : 1, // Sometimes has -1 as count but still has value
      ratingValue
    }
  }

  return data
}

/**
 * Get trailer data if any
 * @param item
 * @param url
 * @param image
 * @param name
 * @returns {{}}
 * @private
 */
function _getTrailer (item, url, image, name) {
  const data = {}

  if (_get(item, 'trailers[0]', null)) {
    const uploadDate = item.releaseDate ? DateTime.fromFormat(item.releaseDate, 'yyyyMMdd') : DateTime.local()

    data.trailer = {
      '@type': 'VideoObject',
      name,
      embedUrl: url,
      thumbnail: {
        '@type': 'ImageObject',
        contentUrl: image
      },
      thumbnailUrl: image,
      description: `${name} - Bande annonce`,
      uploadDate: uploadDate.toFormat('yyyy-MM-dd')
    }
  }

  return data
}

/**
 * Get Country of origin if available
 * @param item
 * @returns {{}}
 * @private
 */
function _getCountryOfOrigin (item) {
  const data = {}

  if (_get(item, 'productionCountries[0]', null)) {
    data.countryOfOrigin = {
      '@type': 'Country',
      name: item.productionCountries[0]
    }
  }

  return data
}

/**
 * Get actors, authors and director, producer if available
 * @param item
 * @returns {{actor: {'@type': string, name: string}[], author: {'@type': string, name: string}[]}}
 * @private
 */
function _getPersons (item) {
  const actors = _get(item, 'actors') || _get(item, 'contributors.actors')
  const authors = _get(item, 'authors')
  const directors = _get(item, 'directors') || _get(item, 'contributors.directors')
  const producers = _get(item, 'producers')

  const data = {
    actor: _getJsonPersonFromList(actors),
    author: _getJsonPersonFromList(authors)
  }

  if (!_isEmpty(directors)) {
    data.director = _getJsonPersonFromList(directors)[0]
  }

  if (!_isEmpty(producers)) {
    data.producer = _getJsonPersonFromList(producers)[0]
  }

  return data
}

/**
 * Get Release date if available
 * @param item
 * @returns {{}}
 * @private
 */
function _getReleaseDate (item) {
  const data = {}

  if (item.releaseDate) {
    data.dateCreated = DateTime.fromFormat(item.releaseDate, 'yyyyMMdd').toFormat('yyyy-MM-dd')
  }

  return data
}

/**
 * Get Release date if available
 * @param item
 * @returns {{}}
 * @private
 */
function _getProductionCompany (item) {
  const data = {}

  if (item.provider) {
    data.productionCompany = {
      '@type': 'Organization',
      name: item.provider
    }
  }

  return data
}

/**
 * Get Part of series info
 * @param item
 * @returns {{}}
 * @private
 */
function _getPartOfSeries (item) {
  const data = {}

  if (item.seriesInfo && item.seriesInfo.title) {
    data.partOfSeries = {
      '@type': 'TVSeries',
      name: item.seriesInfo.title
    }
  }

  return data
}

/**
 * Return content rating from MPAA
 * https://en.wikipedia.org/wiki/Motion_Picture_Association_film_rating_system
 * @param item
 * @returns {string}
 */
function _getContentRating (item) {
  switch (item.csa) {
    case 2:
      return 'PG'
    case 3:
      return 'PG-13'
    case 4:
      return 'R'
    case 5:
      return 'NC-17'
    default:
      return 'G'
  }
}

/**
 *
 * @param persons
 * @returns {{"@type": string, name: string}[]}
 * @private
 */
function _getJsonPersonFromList (persons = []) {
  return persons.map(({ firstName, lastName }) => ({
    '@type': 'Person',
    name: `${firstName} ${lastName}`
  }))
}

/**
 *
 * @param languages
 * @returns {{"@type": string, name: string}[]}
 * @private
 */
function _getJsonLanguagesFromList (languages = []) {
  return languages.map(({ language }) => ({
    '@type': 'Language',
    name: `${language}`
  }))
}

export {
  _decorateTitle,
  _getAggregateRating,
  _getContentRating,
  _getPartOfSeries,
  _getProductionCompany,
  _getJsonLanguagesFromList,
  _getReleaseDate,
  _getPersons,
  _getCountryOfOrigin,
  _getCoverUrl,
  _getFipRouteName,
  _getFipRouteParams,
  _getTrailer
}
